import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { usd, errMsg } from "./shared";

const box = "bg-[#121212] border-white/10";

function CategoryEditor({ value, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const c = draft.trim();
    if (c && !value.includes(c)) onChange([...value, c]);
    setDraft("");
  };
  return (
    <div className="space-y-2" data-testid="market-cfg-categories">
      <p className="text-sm text-white/70">Business categories</p>
      <div className="flex flex-wrap gap-2">
        {value.map((c) => (
          <span key={c} className="inline-flex items-center gap-1 h-7 px-3 rounded-full bg-white/10 text-xs">
            {c}
            <button data-testid={`market-cfg-cat-remove-${c.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => onChange(value.filter((x) => x !== c))} className="text-white/50 hover:text-red-400">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid="market-cfg-cat-input" placeholder="New category" value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} className="bg-white/5 border-white/10" />
        <Button data-testid="market-cfg-cat-add" variant="outline" onClick={add}>Add</Button>
      </div>
    </div>
  );
}

function ConfigCard() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/market/config").then(({ data }) => setCfg(data)); }, []);
  if (!cfg) return null;
  const set = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });
  const save = async () => {
    try {
      const { data } = await api.put("/admin/market/config", {
        credit_usd_rate: Number(cfg.credit_usd_rate), usd_per_point: Number(cfg.usd_per_point),
        points_per_share: Number(cfg.points_per_share), registration_fee_credits: Number(cfg.registration_fee_credits),
        spend_entry_fee_credits: Number(cfg.spend_entry_fee_credits),
        promo_price_per_day: Number(cfg.promo_price_per_day), promo_min_days: Number(cfg.promo_min_days),
        promo_max_days: Number(cfg.promo_max_days), promo_radius_km: Number(cfg.promo_radius_km),
        categories: cfg.categories,
      });
      setCfg(data); toast.success("Market settings saved");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const fields = [
    ["credit_usd_rate", "$ value of 1 purchased Credit"], ["usd_per_point", "$ spent per 1 point"],
    ["points_per_share", "Points per reward share"], ["registration_fee_credits", "Business registration fee (Credits)"],
    ["spend_entry_fee_credits", "Fee per customer spending entry (Credits)"],
    ["promo_price_per_day", "Promotion price per day, per location (Credits)"],
    ["promo_min_days", "Promotion minimum days"], ["promo_max_days", "Promotion maximum days"],
    ["promo_radius_km", "Promotion reach around each location (km)"],
  ];
  return (
    <Card className={box}><CardContent className="p-5 space-y-3">
      <p className="font-heading font-bold text-lg">Market settings</p>
      <div className="grid md:grid-cols-2 gap-3">
        {fields.map(([k, label]) => (
          <label key={k} className="text-sm text-white/70 space-y-1">
            <span>{label}</span>
            <Input data-testid={`market-cfg-${k}`} type="number" step="any" value={cfg[k]} onChange={set(k)} className="bg-white/5 border-white/10" />
          </label>
        ))}
      </div>
      <CategoryEditor value={cfg.categories} onChange={(categories) => setCfg((c) => ({ ...c, categories }))} />
      <Button data-testid="market-cfg-save" onClick={save}>Save settings</Button>
    </CardContent></Card>
  );
}

function RewardCard() {
  const [shares, setShares] = useState(null);
  const [amount, setAmount] = useState("");
  const [history, setHistory] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/market/shares").then(({ data }) => setShares(data));
    api.get("/admin/market/rewards").then(({ data }) => setHistory(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const total = shares?.total_shares || 0;
  const per = total && Number(amount) > 0 ? Number(amount) / total : 0;

  const distribute = async () => {
    if (!window.confirm(`Distribute ${usd(amount)} across ${total} shares (${usd(per)} per share)?`)) return;
    try { await api.post("/admin/market/rewards", { amount: Number(amount) }); toast.success("Reward distributed"); setAmount(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className={box}><CardContent className="p-5 space-y-3">
      <p className="font-heading font-bold text-lg">Reward payout</p>
      <p className="text-sm text-white/70" data-testid="market-admin-total-shares">
        <b className="text-white text-2xl">{total}</b> shares held by <b className="text-white">{shares?.holders_count || 0}</b> users
      </p>
      <div className="flex gap-2">
        <Input data-testid="market-reward-amount" type="number" placeholder="Reward amount in $" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-white/5 border-white/10" />
        <Button data-testid="market-reward-distribute" onClick={distribute} disabled={!per}>Distribute</Button>
      </div>
      {per > 0 && <p className="text-sm text-sky-300" data-testid="market-reward-preview">Each share = <b>{usd(per)}</b></p>}
      <div className="text-xs text-white/60 space-y-1 max-h-48 overflow-y-auto">
        {history.map((h) => (
          <div key={h.distribution_id} className="flex justify-between border-b border-white/5 py-1">
            <span>{new Date(h.created_at).toLocaleString()}</span>
            <span>{usd(h.amount_usd)} ÷ {h.total_shares} = {usd(h.per_share_usd)}/share</span>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

function BusinessesCard() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/market/businesses", { params: filter === "all" ? {} : { status: filter } }).then(({ data }) => setRows(data));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { await api.post(`/admin/market/businesses/${id}/${action}`); toast.success(`Business ${action}d`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className={box}><CardContent className="p-5 space-y-3">
      <p className="font-heading font-bold text-lg">Registered businesses</p>
      <div className="flex flex-wrap gap-2">
        {["pending", "approved", "rejected", "deleted", "all"].map((s) => (
          <Button key={s} size="sm" variant={filter === s ? "default" : "outline"} data-testid={`market-biz-filter-${s}`} onClick={() => setFilter(s)}>{s}</Button>
        ))}
      </div>
      {rows.length === 0 && <p className="text-sm text-white/50">Nothing here.</p>}
      {rows.map((b) => (
        <div key={b.business_id} className="border border-white/10 rounded-lg p-3 flex flex-col md:flex-row md:items-center justify-between gap-2" data-testid={`market-admin-biz-${b.business_id}`}>
          <div className="text-sm">
            <p className="font-bold">{b.name} <span className="text-white/50 font-normal">· {b.deleted ? "deleted" : b.status}</span></p>
            <p className="text-white/60">{b.location} · {b.gender} · {b.age} · owner {b.owner_name} ({b.owner_email}) · fee {b.fee_paid_credits || 0} cr</p>
          </div>
          <div className="flex gap-2 shrink-0">
            {!b.deleted && b.status !== "approved" && <Button size="sm" data-testid={`market-biz-approve-${b.business_id}`} onClick={() => act(b.business_id, "approve")}>Approve</Button>}
            {!b.deleted && b.status === "pending" && <Button size="sm" variant="outline" data-testid={`market-biz-reject-${b.business_id}`} onClick={() => act(b.business_id, "reject")}>Reject</Button>}
            {b.deleted
              ? <Button size="sm" variant="outline" data-testid={`market-biz-restore-${b.business_id}`} onClick={() => act(b.business_id, "restore")}>Restore</Button>
              : <Button size="sm" variant="destructive" data-testid={`market-biz-delete-${b.business_id}`} onClick={() => act(b.business_id, "delete")}>Delete</Button>}
          </div>
        </div>
      ))}
    </CardContent></Card>
  );
}

function SpendLogCard() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/market/spend-log").then(({ data }) => setRows(data)); }, []);
  return (
    <Card className={box}><CardContent className="p-5">
      <p className="font-heading font-bold text-lg mb-2">Business spending log</p>
      <div className="max-h-80 overflow-y-auto text-sm" data-testid="market-spend-log">
        {rows.length === 0 && <p className="text-white/50">No entries yet.</p>}
        {rows.map((e) => (
          <div key={e.entry_id} className="flex justify-between gap-2 border-b border-white/5 py-1.5">
            <span className="text-white/80">{e.business_name} → {e.customer_name} <span className="text-white/40">{e.note}</span></span>
            <span className={e.amount < 0 ? "text-red-400" : "text-emerald-400"}>{e.amount < 0 ? "-" : "+"}{usd(Math.abs(e.amount))} · {new Date(e.created_at).toLocaleDateString()}</span>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
}

const refundFor = (p) => {
  const fullDays = Math.max(0, Math.floor((new Date(p.ends_at) - Date.now()) / 86400000));
  return Math.floor((fullDays * p.cost) / Math.max(1, p.days));
};

function PromotionsCard() {
  const [status, setStatus] = useState("active");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/admin/market/promotions", { params: { status } }).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);
  const stop = async (p, refund) => {
    const amt = refundFor(p);
    const msg = refund ? `Stop ${p.business_name}'s promotion and refund ${amt} Credits for unused days?`
      : `Stop ${p.business_name}'s promotion now without a refund?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/admin/market/promotions/${p.promo_id}/stop`, { refund });
      toast.success(data.refunded_credits ? `Stopped · ${data.refunded_credits} Credits refunded` : "Promotion stopped");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const now = new Date().toISOString();
  return (
    <Card className={box} data-testid="admin-promotions"><CardContent className="p-5 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="font-heading font-bold text-lg">Promotions</p>
        <div className="flex gap-2">
          {["active", "all"].map((s) => (
            <Button key={s} size="sm" variant={status === s ? "default" : "outline"} data-testid={`admin-promotions-filter-${s}`} onClick={() => setStatus(s)}>
              {s === "active" ? "Running" : "All"}
            </Button>
          ))}
        </div>
      </div>
      {rows.length === 0 && <p className="text-sm text-white/50">No promotions.</p>}
      {rows.map((p) => {
        const live = p.ends_at > now;
        return (
          <div key={p.promo_id} data-testid={`admin-promo-${p.promo_id}`} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 py-2 last:border-0 text-sm">
            <div>
              <p className="font-bold">{p.business_name} <span className="text-white/50 font-normal">· {p.locations.join(", ")}</span></p>
              <p className="text-white/50 text-xs">
                {p.days} day(s) · {p.cost} Credits · {p.views || 0} views · {p.taps || 0} taps ·{" "}
                {live ? `ends ${new Date(p.ends_at).toLocaleString()}` : p.stopped_by_admin ? "stopped by admin" : "ended"}
              </p>
            </div>
            {live && (
              <div className="flex gap-2">
                <Button size="sm" variant="destructive" data-testid={`admin-promo-stop-${p.promo_id}`} onClick={() => stop(p, false)}>Stop</Button>
                <Button size="sm" variant="outline" data-testid={`admin-promo-stop-refund-${p.promo_id}`} onClick={() => stop(p, true)}>
                  Stop + refund {refundFor(p)}
                </Button>
              </div>
            )}
            {!live && p.refunded_credits > 0 && <span className="text-xs text-emerald-400">Refunded {p.refunded_credits}</span>}
          </div>
        );
      })}
    </CardContent></Card>
  );
}

export function MarketAdminPanel() {
  return (
    <div className="space-y-5" data-testid="market-admin-panel">
      <div className="grid lg:grid-cols-2 gap-5"><RewardCard /><ConfigCard /></div>
      <PromotionsCard />
      <BusinessesCard />
      <SpendLogCard />
    </div>
  );
}
